import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Card } from '@abyss/web/ui/Card';
import { CollapseProvider } from '@abyss/web/ui/CollapseProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../../hooks/useLagoon';
import { FacilityDetails } from '../../../models/FacilityDetails';
import { useAnalyticsStore } from '../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../store/useAnalyticsStore/analyticsStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { Constants } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { mobileOnly } from '../../ConstantsStyles';
import { LinkContainer } from '../../LinkContainer/LinkContainer';
import { AoeDrawer } from '../../ProviderDetailTabs/About/CollapseProviderDetailsSection/AoeDrawer';
import { getFeatureFlag } from '../../Utils';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
  convertSpecialtiesList,
  getSearchBlock,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { extractRollupCodesParts } from '../Utils';
import {
  CollapseCardHeaderStyle,
  CollapseSection,
  SectionContainer,
  StyledContentText,
  StyledHeadingText,
  TextContainer,
} from './FacilityDetailsAboutSection.styled';

type Props = {
  facilityDetails: FacilityDetails;
  tabTitle?: string;
  coverageType: string;
};

export const FacilityDetailsAboutSection = ({
  facilityDetails,
  tabTitle,
  coverageType,
}: Props) => {
  const mobile = useMediaQuery(mobileOnly);
  const { t } = useTranslation();

  // AOE Drawer state and feature flag
  const [isAoeDrawerOpen, setIsAoeDrawerOpen] = useState(false);
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const lagoonData = useLagoon('ui-messaging')();

  const isAoeDescriptionDrawerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AOE_DESCRIPTION_DRAWER
  );

  const aoeDescriptionButtonData = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.AOE_DESCRIPTION_BUTTON_TEXT,
  });

  const areaOfExpertise = Array.isArray(facilityDetails?.areaOfExpertise)
    ? facilityDetails.areaOfExpertise.filter(Boolean)
    : [];
  const areaOfExpertiseDetails = Array.isArray(
    facilityDetails?.areaOfExpertiseDetails
  )
    ? facilityDetails.areaOfExpertiseDetails.filter(
        (item) => item?.name || item?.description
      )
    : [];
  const areaOfExpertiseList =
    areaOfExpertise.length > 0
      ? areaOfExpertise
      : areaOfExpertiseDetails.map((item) => item?.name).filter(Boolean);
  const isAoeSectionVisible = areaOfExpertiseList.length > 0;
  const isAoeDrawerVisible =
    isAoeDescriptionDrawerEnabled && areaOfExpertiseDetails.length > 0;
  const aoeLinkText =
    aoeDescriptionButtonData?.message ||
    t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.WHAT_IS_THIS') ||
    'What is this?';

  const organizationTypes = extractRollupCodesParts(
    facilityDetails?.organizationType
  );

  const defaultTranslatedOrgTypes = extractRollupCodesParts(
    facilityDetails?.defaultTranslatedValues?.organizationType
  );

  const { typeaheadState } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      typeaheadState: state.typeaheadSearchStore,
    }))
  );
  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      facilityDetails.providerType
    )} details`,
    impressionBlock: {
      type: convertProviderTypeToAdobeType(facilityDetails.providerType),
      indicator: '',
    },
    providerBlock: {
      type: convertCoverageType(coverageType),
      specialty: convertSpecialtiesList(defaultTranslatedOrgTypes),
      role: 'facility',
    },
    searchBlock: getSearchBlock(typeaheadState, linkName),
  });

  useEffect(() => {
    if (facilityDetails.providerName) adobePageTrackEvent();
  }, [facilityDetails]);

  return (
    <React.Fragment>
      <CollapseSection>
        {mobile ? (
          <CollapseProvider>
            <Card
              collapse
              css={CollapseCardHeaderStyle}
              header={
                <Heading
                  css={{
                    '&.abyss-heading-root': {
                      fontSize: '20.25px !important',
                      lineHeight: '24px !important',
                    },
                  }}
                  display="h4"
                  offset={1}
                >
                  {t(Constants.FACILITY_DETAILS_ABOUT_SECTION.SPECIALTY)}
                </Heading>
              }
            >
              <Card.Section
                css={{
                  'abyss-card-section': {
                    paddingLeft: '0px',
                    paddingTop: '0px',
                    paddingBottom: '$md',
                  },
                }}
              >
                <TextContainer>
                  {organizationTypes?.map((organizationType) => (
                    <StyledHeadingText key={`organization_${organizationType}`}>
                      {organizationType}
                    </StyledHeadingText>
                  ))}
                </TextContainer>
              </Card.Section>
            </Card>
          </CollapseProvider>
        ) : (
          <SectionContainer>
            <Heading
              css={{ marginBottom: '12px' }}
              data-auto-testid="specialty-heading"
              data-testid="specialty-heading"
              display="h4"
              offset={1}
            >
              {t(Constants.FACILITY_DETAILS_ABOUT_SECTION.SPECIALTY)}
            </Heading>
            {organizationTypes?.map((organizationType) => (
              <StyledContentText
                data-auto-testid="specialty-type"
                data-testid="specialty-type"
                key={`organization_${organizationType}`}
              >
                {organizationType}
              </StyledContentText>
            ))}
          </SectionContainer>
        )}
      </CollapseSection>

      {/* Areas of Expertise Section */}
      {isAoeSectionVisible && (
        <CollapseSection>
          {mobile ? (
            <CollapseProvider>
              <Card
                collapse
                css={CollapseCardHeaderStyle}
                header={
                  <Heading
                    css={{
                      '&.abyss-heading-root': {
                        fontSize: '20.25px !important',
                        lineHeight: '24px !important',
                      },
                    }}
                    display="h4"
                    offset={1}
                  >
                    {t(
                      'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.AREAS_OF_EXPERTISE'
                    )}
                  </Heading>
                }
              >
                <Card.Section
                  css={{
                    'abyss-card-section': {
                      paddingLeft: '0px',
                      paddingTop: '0px',
                      paddingBottom: '$md',
                    },
                  }}
                >
                  <TextContainer>
                    {areaOfExpertiseList.map((aoe, index) => (
                      <StyledHeadingText key={`aoe_${index}`}>
                        {aoe}
                      </StyledHeadingText>
                    ))}
                  </TextContainer>

                  {/* AOE Drawer Link - Mobile */}
                  {isAoeDrawerVisible && (
                    <LinkContainer
                      analyticsInfo={{ location: 'body:areas of expertise' }}
                      before={
                        <IconMaterial
                          icon="info"
                          size="$sm"
                          variant="outlined"
                        />
                      }
                      css={{ marginTop: '16px' }}
                      data-auto-testid="view-definition-aoe-facility-mobile"
                      data-testid="view-definition-aoe-facility-mobile"
                      onClick={() => setIsAoeDrawerOpen(true)}
                      size="$sm"
                      text={aoeLinkText}
                    />
                  )}
                </Card.Section>
              </Card>
            </CollapseProvider>
          ) : (
            <SectionContainer>
              <Heading
                css={{ marginBottom: '12px' }}
                data-auto-testid="areas-of-expertise-heading"
                data-testid="areas-of-expertise-heading"
                display="h4"
                offset={1}
              >
                {t(
                  'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.AREAS_OF_EXPERTISE'
                )}
              </Heading>
              {areaOfExpertiseList.map((aoe, index) => (
                <StyledContentText
                  data-auto-testid="area-of-expertise-item"
                  data-testid="area-of-expertise-item"
                  key={`aoe_${index}`}
                >
                  {aoe}
                </StyledContentText>
              ))}

              {/* AOE Drawer Link - Desktop */}
              {isAoeDrawerVisible && (
                <LinkContainer
                  analyticsInfo={{ location: 'body:areas of expertise' }}
                  before={
                    <IconMaterial icon="info" size="$sm" variant="outlined" />
                  }
                  css={{ marginTop: '16px' }}
                  data-auto-testid="view-definition-aoe-facility"
                  data-testid="view-definition-aoe-facility"
                  onClick={() => setIsAoeDrawerOpen(true)}
                  size="$md"
                  text={aoeLinkText}
                />
              )}
            </SectionContainer>
          )}

          {/* AOE Drawer Component */}
          {isAoeDrawerVisible && (
            <AoeDrawer
              areaOfExpertiseDetails={areaOfExpertiseDetails}
              isOpen={isAoeDrawerOpen}
              setIsOpen={setIsAoeDrawerOpen}
            />
          )}
        </CollapseSection>
      )}
    </React.Fragment>
  );
};
